import React, { DetailedHTMLProps, SelectHTMLAttributes } from "react";

import { Link } from "react-router-dom";

import { AvatarIcon, BuildingIcon, ContractIcon } from "../icons";
import Tooltip from "./components/Tooltip";

// prettier-ignore
type TailwindSize = 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 |  14 | 15 | 16 | 20 | 24 | 28 | 32  | 36 | 40 | 44 | 48;

type AvatarPlaceholderProps = {
  name?: string;
  size?: TailwindSize;
  reverse?: boolean;
  type?: "user" | "host" | "company" | "document";
} & DetailedHTMLProps<SelectHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

const AvatarPlaceholder = React.forwardRef<
  HTMLDivElement,
  AvatarPlaceholderProps
>(({ name, size, type, reverse = false }, ref) => {
  return (
    <>
      {type === "user" && (
        <div
          ref={ref}
          className={`ring-2 ring-white inline-block w-${size} h-${size} rounded-full shadow-sm overflow-hidden ${
            reverse ? "bg-dark-100" : "bg-light-0"
          }`}
        >
          <AvatarIcon
            iconClassName={`w-full h-full ${
              reverse ? "text-light-0" : "text-dark-100"
            }`}
          />
        </div>
      )}
      {type === "document" && (
        <div
          ref={ref}
          className={`ring-2 ring-white w-${size} h-${size} rounded-full ${
            reverse ? "bg-dark-100" : "bg-light-0"
          } shadow-sm inline-flex items-center justify-center`}
        >
          <ContractIcon
            iconClassName={`${
              reverse ? "text-light-0" : "text-dark-100"
            } w-6 h-6`}
          />
        </div>
      )}
      {type === "host" && (
        <div
          ref={ref}
          className={`ring-2 ring-white w-${size} h-${size} rounded-full ${
            reverse ? "bg-dark-100" : "bg-light-0"
          } shadow-sm inline-flex items-center justify-center`}
        >
          <BuildingIcon
            iconClassName={`${
              reverse ? "text-light-0" : "text-dark-100"
            } w-6 h-6`}
          />
        </div>
      )}
      {type === "company" && (
        <div
          ref={ref}
          className={`ring-2 ring-white w-${size} h-${size} rounded-full ${
            reverse ? "bg-dark-100" : "bg-light-0"
          } shadow-sm inline-flex items-center justify-center`}
        >
          <BuildingIcon
            iconClassName={`${
              reverse ? "text-light-0" : "text-dark-100"
            } w-6 h-6`}
          />
        </div>
      )}
    </>
  );
});

export type AvatarProps = {
  src?: string;
  name?: string;
  size?: TailwindSize;
  alt: string;
  reverse?: boolean;
  type?: "user" | "host" | "company" | "document";
} & DetailedHTMLProps<SelectHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      src,
      name = "",
      type = "user",
      size = 12,
      alt,
      reverse = false,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div className={`flex-shrink-0 inline-block ${className}`} ref={ref}>
        {src && (
            <img
              className={`w-${size} h-${size} rounded-full object-cover shadow-sm`}
              src={src}
              alt={alt}
              {...props}
            />
        )}
        {!src && (
          <AvatarPlaceholder
            type={type}
            size={size}
            name={name}
            reverse={reverse}
          />
        )}
      </div>
    );
  }
);

export type AvatarGroupProps = {
  items: Omit<AvatarProps, "size">[];
  size?: TailwindSize;
  max?: number;
} & DetailedHTMLProps<SelectHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  ({ items, size = 6, max = 5, ...props }, ref) => {
    return (
      <div className="flex flex-shrink-0 sm:mt-0">
        <Tooltip label={items.map(({ name }) => name).join(", ")}>
          <div className="flex -space-x-2 overflow-hidden" ref={ref}>
            {items.map(({ src, alt, name, type, ...itemProps }, index) =>
              index > max - 1 ? null : src ? (
                <img
                  key={`${alt}-${index}`}
                  className={`inline-block w-${size} h-${size} ${
                    index === 0 ? "" : "-ml-1"
                  } rounded-full ring-2 ring-white object-cover`}
                  src={src}
                  alt={alt}
                  name={name}
                  {...props}
                  {...itemProps}
                />
              ) : (
                <AvatarPlaceholder
                  type={type}
                  size={size}
                  name={name}
                  key={`${alt}-${index}`}
                />
              )
            )}
          </div>
        </Tooltip>
        {items.length > max && (
          <span className="flex flex-col justify-center ml-1 text-xs text-dark-100">
            + {items.length - max}
          </span>
        )}
      </div>
    );
  }
);

type AvatarLinkProps = {
  src?: string;
  primaryText?: string;
  secondaryText?: string;
  size?: TailwindSize;
  alt: string;
  to: string;
  type?: "user" | "host" | "company";
} & DetailedHTMLProps<
  SelectHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>;

export const AvatarLink = React.memo(
  React.forwardRef<HTMLAnchorElement, AvatarLinkProps>(
    (
      {
        src,
        type = "user",
        name,
        size = 12,
        alt,
        to,
        primaryText,
        secondaryText,
      },
      ref
    ) => {
      return (
        <Link
          to={to}
          className="flex-shrink-0 block group focus:outline-none"
          ref={ref}
        >
          <div className="flex items-center">
            {src && (
              <img
                className={`w-${size} h-${size} rounded-full object-cover`}
                src={src}
                alt={alt}
              />
            )}
            {!src && <AvatarPlaceholder type={type} size={size} name={name} />}
            <div className="ml-3">
              <p className="text-sm font-medium leading-5 text-gray-700 group-hover:text-gray-900">
                {primaryText}
              </p>
              <p className="text-xs font-medium leading-4 text-gray-500 transition duration-150 ease-in-out group-hover:text-gray-700 group-focus:underline">
                {secondaryText}
              </p>
            </div>
          </div>
        </Link>
      );
    }
  )
);

export default React.memo(Avatar);
